/**
 * TeamPage — "Équipe & permissions": the lab-scoped staff roster + the per-member permission editor
 * (preset bundles + ownership + fine-grained grant/deny deltas). The deny-wins posture is surfaced in
 * the permissions drawer (a `deny` delta is badged danger and always overrides). Every write delegates
 * to iam via the lab-admin staff endpoints; the server is the trust boundary (it re-checks the cap, the
 * preset validity, the catalog grant, and invalidates the live capability cache).
 *
 * Gating: this whole surface is owner/manager-only via iam.utilisateur.manage. The route reaches the
 * shell for any authed user (empty module gate), so the screen renders its own ForbiddenState when the
 * cap is missing; the nav item is hidden by the same cap. Full 5-state DoD.
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Drawer,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ADMIN_CAPS } from "../caps";
import { useLabId, useStaff, type StaffLifecycleAction } from "../api";
import type { StaffMember } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { CreateStaffModal } from "../components/CreateStaffModal";
import { PermissionsDrawer } from "../components/PermissionsDrawer";
import { StaffLifecycleModal } from "../components/StaffLifecycleModal";
import { formatDateTime } from "../lib";

/** Utilisateur.statut → badge tone + i18n label key. Reflects the NOVA-031 lifecycle (actif/suspendu/archive). */
const STATUT_BADGE: Record<string, { tone: "success" | "warn" | "neutral"; glyph: string; key: string }> = {
  actif: { tone: "success", glyph: "●", key: "admin.team.statut.actif" },
  suspendu: { tone: "warn", glyph: "❙❙", key: "admin.team.statut.suspendu" },
  archive: { tone: "neutral", glyph: "▱", key: "admin.team.statut.archive" },
};

export function TeamPage() {
  const { t, locale } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canManage = useCan(ADMIN_CAPS.staffManage);

  const query = useStaff(labId, canManage && online);
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [permsMember, setPermsMember] = useState<StaffMember | null>(null);
  // NOVA-031 — the staff-lifecycle confirm target (member + chosen suspend/revoke/archive action).
  const [lifecycle, setLifecycle] = useState<{ member: StaffMember; action: StaffLifecycleAction } | null>(null);

  // Keep the open drawer in sync with the freshly-fetched roster (grants change after a write).
  const liveMember = useMemo(
    () => (permsMember ? rows.find((r) => r.staffId === permsMember.staffId) ?? permsMember : null),
    [rows, permsMember],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.utilisateur.nomComplet.toLowerCase().includes(q) ||
        r.utilisateur.email.toLowerCase().includes(q) ||
        r.presets.some((p) => p.toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const columns: ReadonlyArray<Column<StaffMember>> = [
    {
      key: "name",
      header: t("admin.team.col.name"),
      sortable: true,
      sortAccessor: (r) => r.utilisateur.nomComplet,
      cell: (r) => r.utilisateur.nomComplet || "—",
    },
    {
      key: "email",
      header: t("admin.team.col.email"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.utilisateur.email}</span>,
    },
    {
      key: "presets",
      header: t("admin.team.col.presets"),
      cell: (r) =>
        r.presets.length ? (
          <div style={{ display: "inline-flex", gap: "var(--nv-s-1)", flexWrap: "wrap" }}>
            {r.presets.map((p) => (
              <Badge key={p} tone="info" glyph="▣">
                {t(`admin.team.preset.${p}`)}
              </Badge>
            ))}
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "owner",
      header: t("admin.team.col.owner"),
      cell: (r) =>
        r.estProprietaire ? (
          <Badge tone="warn" glyph="★">
            {t("admin.team.owner.yes")}
          </Badge>
        ) : (
          <Badge tone="neutral" glyph="○">
            {t("admin.team.owner.no")}
          </Badge>
        ),
    },
    {
      key: "statut",
      header: t("admin.team.col.statut"),
      cell: (r) => {
        const b = STATUT_BADGE[r.utilisateur.statut] ?? {
          tone: "neutral" as const,
          glyph: "○",
          key: "admin.team.statut.inactif",
        };
        return (
          <Badge tone={b.tone} glyph={b.glyph}>
            {t(b.key)}
          </Badge>
        );
      },
    },
    {
      key: "lastLogin",
      header: t("admin.team.col.lastLogin"),
      cell: (r) => <span className="nv-ltr-island">{formatDateTime(r.utilisateur.dernierLoginAt, locale)}</span>,
    },
    {
      key: "actions",
      header: t("admin.team.col.actions"),
      align: "end",
      cell: (r) => {
        const archived = r.utilisateur.statut === "archive";
        const suspended = r.utilisateur.statut === "suspendu";
        return (
          <div style={{ display: "inline-flex", gap: "var(--nv-s-1)", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button size="sm" variant="secondary" onClick={() => setPermsMember(r)}>
              {t("admin.team.manage")}
            </Button>
            {/* NOVA-031 — owner-gated lifecycle trio. Suspend is offered only on an active member;
                Revoke/Archive remain available until the account is already archived (terminal). */}
            {!archived && !suspended && (
              <Button size="sm" variant="ghost" onClick={() => setLifecycle({ member: r, action: "suspend" })}>
                {t("admin.team.lifecycle.suspend")}
              </Button>
            )}
            {!archived && (
              <Button size="sm" variant="ghost" onClick={() => setLifecycle({ member: r, action: "revoke" })}>
                {t("admin.team.lifecycle.revoke")}
              </Button>
            )}
            {!archived && (
              <Button size="sm" variant="danger" onClick={() => setLifecycle({ member: r, action: "archive" })}>
                {t("admin.team.lifecycle.archive")}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const header = (
    <PageHeader
      title={t("admin.team.title")}
      subtitle={t("admin.team.subtitle")}
      actions={<Button onClick={() => setCreateOpen(true)}>{t("admin.team.new")}</Button>}
    />
  );

  if (!canManage) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.staffManage]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)", borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))" }}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("action.search")}
            aria-label={t("action.search")}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.staffId}
            caption={t("admin.team.title")}
            pageSize={12}
            emptyTitle={search.trim() ? t("admin.empty.none") : t("admin.team.empty.title")}
            {...(search.trim() ? {} : { emptyDescription: t("admin.team.empty.desc") })}
            emptyAction={
              !search.trim() ? (
                <Button onClick={() => setCreateOpen(true)}>{t("admin.team.new")}</Button>
              ) : undefined
            }
          />
        </div>
      </Card>

      <CreateStaffModal open={createOpen} onClose={() => setCreateOpen(false)} labId={labId} />

      <StaffLifecycleModal
        member={lifecycle?.member ?? null}
        action={lifecycle?.action ?? null}
        labId={labId}
        onClose={() => setLifecycle(null)}
      />

      <Drawer
        open={!!liveMember}
        onClose={() => setPermsMember(null)}
        title={liveMember ? t("admin.team.perms.title", { name: liveMember.utilisateur.nomComplet }) : ""}
      >
        {liveMember && <PermissionsDrawer member={liveMember} labId={labId} />}
      </Drawer>
    </Screen>
  );
}
